import React, { useState } from "react";

import {
  Bell,
  MapPin,
  Phone,
  Mail,
  Clock,
  ChevronDown,
} from "lucide-react";

import {
  FaWhatsapp,
  FaInstagram,
  FaLinkedinIn,
  FaXTwitter,
} from "react-icons/fa6";

import { motion, AnimatePresence } from "framer-motion";

import WBLogo from "../../Assets/Images/WB.png";
import WBBlackLogo from "../../Assets/Images/WBLogo.png";

import WavyText from "../animations/WavyText.jsx";
import { useNavigate } from "react-router-dom";

/* ============================================================
   HOVER TRANSITION
============================================================ */

const hoverTransition = {
  duration: 0.28,
  ease: "easeOut",
};

/* ============================================================
   HOVER LIST ITEM
============================================================ */

const HoverLi = ({ className = "", children, ...props }) => (
  <motion.li
    whileHover={{ x: 5 }}
    whileTap={{ scale: 0.97 }}
    transition={hoverTransition}
    className={`cursor-pointer ${className}`}
    {...props}
  >
    {children}
  </motion.li>
);

/* ============================================================
   SERVICE DATA
============================================================ */

const serviceGroups = [
  {
    id: "software",
    title: "Software Development",
    path: "/services/software-development",
    subServices: [
      {
        title: "Custom Software Development",
        path: "/services/software-development/custom-software",
      },
      {
        title: "Mobile App Development",
        path: "/services/software-development/mobile-app-development",
      },
      {
        title: "Web Development",
        path: "/services/software-development/web-development",
      },
      {
        title: "UI/UX Development",
        path: "/services/software-development/ui-ux-development",
      },
      {
        title: "CRM Solutions",
        path: "/services/software-development/crm-development",
      },
      {
        title: "MLM Software",
        path: "/services/software-development/mlm-software",
      },
    ],
  },

  {
    id: "api",
    title: "API Services",
    path: "/services/api",
    subServices: [
      {
        title: "Business SMS Services",
        path: "/services/api/whatsapp-api",
      },
      {
        title: "Secure OTP Services",
        path: "/services/api/sms-otp",
      },
    ],
  },

  {
    id: "legal",
    title: "Legal Services",
    path: "/services/legal-services",
    subServices: [
      {
        title: "Business Registration",
        path: "/services/legal-services/business-registration",
      },
      {
        title: "Company Registration",
        path: "/services/legal-services/company-registration",
      },
      {
        title: "Taxation Services",
        path: "/services/legal-services/taxation-services",
      },
      {
        title: "ISO Certification",
        path: "/services/legal-services/iso-sertification",
      },
      {
        title: "IPR Services",
        path: "/services/legal-services/ipr-services",
      },
    ],
  },
];

/* ============================================================
   FOOTER
============================================================ */

const UserFooter = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const [openService, setOpenService] = useState(null);

  /* ============================================================
     NAVIGATION
  ============================================================ */

  const handleNavigate = (path) => {
    navigate(path);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ============================================================
     SERVICE DROPDOWN
  ============================================================ */

  const toggleService = (serviceId) => {
    setOpenService((previous) =>
      previous === serviceId ? null : serviceId
    );
  };

  return (
    <>
      {/* ============================================================
          FOOTER THEME + LOGO OVERRIDE
      ============================================================ */}

      <style>
        {`
          .footer-content-card {
            background-color: var(--card-bg);
          }

          /* ========================================================
             DARK FOOTER CARD
          ======================================================== */

          [data-theme="dark"] .footer-content-card,
          .dark .footer-content-card,
          body.dark .footer-content-card {
            background-color: #000000 !important;
          }

          /* ========================================================
             LOGO THEME SWITCH

             Light theme:
             -> WBLogo.png
             -> Black logo

             Dark theme:
             -> WB.png
             -> White logo
          ======================================================== */

          .footer-logo-light {
            display: block;
          }

          .footer-logo-dark {
            display: none;
          }

          /* Dark mode */

          [data-theme="dark"] .footer-logo-light,
          .dark .footer-logo-light,
          body.dark .footer-logo-light {
            display: none;
          }

          [data-theme="dark"] .footer-logo-dark,
          .dark .footer-logo-dark,
          body.dark .footer-logo-dark {
            display: block;
          }
        `}
      </style>

      <footer
        className="
          relative
          w-full
          overflow-hidden
          font-saira
          px-4
          py-5
          min-[375px]:px-5
          min-[375px]:py-6
          sm:px-6
          sm:py-7
          md:px-8
          md:py-8
          lg:px-10
          lg:py-9
          xl:px-12
        "
        style={{
          backgroundColor: "var(--bg-secondary)",
          color: "var(--text-primary)",
        }}
      >
        {/* ============================================================
            SUBSCRIBE BANNER
        ============================================================ */}

        <div
          className="
            relative
            z-10
            mx-auto
            flex
            w-full
            max-w-[1500px]
            flex-col
            items-center
            gap-6
            pt-1
            text-center
            sm:gap-7
            sm:pt-2
            md:gap-8
            md:pt-4
            lg:flex-row
            lg:items-center
            lg:justify-between
            lg:gap-10
            lg:text-left
            xl:pt-5
          "
        >
          {/* HEADING */}

          <div
            className="
              min-w-0
              max-w-full
              lg:max-w-[620px]
              xl:max-w-[700px]
            "
          >
            <h2
              className="
                text-[27px]
                font-semibold
                leading-[1.08]
                tracking-tight
                min-[375px]:text-[29px]
                sm:text-3xl
                md:text-4xl
                lg:text-[42px]
                xl:text-5xl
                2xl:text-6xl
              "
              style={{
                color: "var(--text-primary)",
              }}
            >
              <WavyText text="Subscribe now to get" />

              <br />

              <span
                style={{
                  color: "var(--brand-primary)",
                }}
              >
                <WavyText text="latest updates." />
              </span>
            </h2>
          </div>

          {/* SUBSCRIBE FORM */}

          <div
            className="
              flex
              w-full
              max-w-[430px]
              flex-col
              gap-3
              sm:gap-4
            "
          >
            <div
              className="
                flex
                min-h-[52px]
                w-full
                items-center
                rounded-xl
                border
                px-3
                py-2
                sm:min-h-[54px]
                sm:px-4
                md:min-h-[56px]
              "
              style={{
                backgroundColor: "var(--card-bg)",
                borderColor: "var(--border-primary)",
                boxShadow: "0 0 30px var(--shadow-color)",
              }}
            >
              <input
                type="email"
                placeholder="Enter email here..."
                aria-label="Email address"
                className="
                  min-w-0
                  flex-1
                  bg-transparent
                  px-1
                  text-sm
                  outline-none
                  sm:text-base
                "
                style={{
                  color: "var(--text-primary)",
                }}
              />

              <motion.button
                variants={{
                  hover: {
                    scale: 1.08,
                  },
                }}
                whileHover="hover"
                whileTap={{
                  scale: 0.94,
                }}
                className="
                  group
                  ml-2
                  inline-flex
                  h-9
                  w-9
                  shrink-0
                  items-center
                  justify-center
                  rounded-lg
                  transition-all
                  duration-300
                  sm:ml-3
                  sm:h-10
                  sm:w-10
                  focus:outline-none
                  focus:ring-2
                "
                style={{
                  backgroundColor: "var(--brand-primary)",
                  color: "#000000",
                }}
                aria-label="Subscribe"
                type="button"
              >
                <motion.span
                  variants={{
                    hover: {
                      rotate: [0, -15, 12, -8, 6, 0],
                      transition: {
                        duration: 0.6,
                      },
                    },
                  }}
                  className="flex"
                >
                  <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                </motion.span>
              </motion.button>
            </div>

            {/* TERMS */}

            <label
              className="
                flex
                items-start
                justify-center
                gap-2
                text-[11px]
                leading-5
                sm:text-xs
                md:text-sm
                lg:justify-start
              "
              style={{
                color: "var(--text-secondary)",
              }}
            >
              <input
                type="checkbox"
                className="
                  mt-1
                  shrink-0
                "
                style={{
                  accentColor: "var(--brand-primary)",
                }}
              />

              <span>
                Agree to our{" "}
                <button
                  type="button"
                  onClick={() =>
                    handleNavigate("/terms-conditions")
                  }
                  className="
                    underline
                    underline-offset-2
                    transition-colors
                    duration-300
                    hover:opacity-80
                  "
                  style={{
                    color: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color =
                      "var(--brand-primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "inherit";
                  }}
                >
                  Terms &amp; Condition
                </button>
                ?
              </span>
            </label>
          </div>
        </div>

        {/* ============================================================
            FOOTER CONTENT CARD
        ============================================================ */}

        <div
          className="
            relative
            z-10
            mx-auto
            w-full
            max-w-[1500px]
            pt-6
            sm:pt-8
            md:pt-10
            lg:pt-12
            pb-3
            sm:pb-4
            md:pb-6
          "
        >
          <div
            className="
              footer-content-card
              rounded-2xl
              border
              px-4
              py-8
              min-[375px]:px-5
              sm:rounded-[22px]
              sm:px-6
              sm:py-9
              md:px-7
              md:py-10
              lg:px-8
              lg:py-10
              xl:px-10
              xl:py-11
            "
            style={{
              backgroundColor: "var(--card-bg)",
              borderColor: "var(--border-primary)",
              boxShadow: "0 10px 40px var(--shadow-color)",
            }}
          >
            <div
              className="
                grid
                grid-cols-1
                gap-9
                sm:grid-cols-2
                sm:gap-x-8
                sm:gap-y-10
                lg:grid-cols-4
                lg:gap-7
                lg:items-start
                xl:gap-10
              "
            >
              {/* ========================================================
                  BRAND
              ======================================================== */}

              <div
                className="
                  flex
                  min-w-0
                  flex-col
                  items-center
                  text-center
                  sm:col-span-2
                  lg:col-span-1
                  lg:items-start
                  lg:text-left
                "
              >
                {/* LOGO */}

                <div
                  className="
                    flex
                    h-[92px]
                    w-full
                    items-center
                    justify-center
                    min-[375px]:h-[98px]
                    sm:h-[104px]
                    md:h-[108px]
                    lg:h-[100px]
                    lg:justify-start
                    xl:h-[106px]
                  "
                >
                  {/* LIGHT THEME - BLACK LOGO */}

                  <img
                    src={WBBlackLogo}
                    alt="WebBocket"
                    className="
                      footer-logo-light
                      h-auto
                      w-[245px]
                      max-w-full
                      object-contain
                      object-left
                      min-[375px]:w-[265px]
                      sm:w-[225px]
                      md:w-[240px]
                      lg:w-[225px]
                      xl:w-[240px]
                    "
                  />

                  {/* DARK THEME - WHITE LOGO */}

                  <img
                    src={WBLogo}
                    alt="WebBocket"
                    className="
                      footer-logo-dark
                      h-auto
                      w-[245px]
                      max-w-full
                      object-contain
                      object-left
                      min-[375px]:w-[265px]
                      sm:w-[225px]
                      md:w-[240px]
                      lg:w-[225px]
                      xl:w-[240px]
                    "
                  />
                </div>

                {/* DESCRIPTION */}

                <p
                  className="
                    mt-1
                    max-w-[340px]
                    text-xs
                    leading-6
                    sm:text-sm
                    sm:leading-7
                    lg:max-w-[270px]
                  "
                  style={{
                    color: "var(--text-muted)",
                  }}
                >
                  Bringing talent and opportunities together. We
                  craft scalable digital solutions for modern
                  businesses.
                </p>

                {/* SOCIAL ICONS */}

                <div
                  className="
                    mt-5
                    flex
                    flex-wrap
                    items-center
                    justify-center
                    gap-2.5
                    sm:mt-6
                    sm:gap-3
                    lg:justify-start
                  "
                >
                  {/* WHATSAPP */}

                  <a
                    href="https://wa.me/7008065931"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="WhatsApp"
                    className="
                      group
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      hover:-translate-y-1
                    "
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      borderColor: "var(--border-primary)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--brand-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--brand-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--border-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--bg-tertiary)";
                    }}
                  >
                    <FaWhatsapp
                      className="
                        text-base
                        transition-colors
                        duration-300
                        group-hover:text-black
                      "
                      style={{
                        color: "var(--text-muted)",
                      }}
                    />
                  </a>

                  {/* INSTAGRAM */}

                  <a
                    href="https://www.instagram.com/web__bocket/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="
                      group
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      hover:-translate-y-1
                    "
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      borderColor: "var(--border-primary)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--brand-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--brand-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--border-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--bg-tertiary)";
                    }}
                  >
                    <FaInstagram
                      className="
                        text-base
                        transition-colors
                        duration-300
                        group-hover:text-black
                      "
                      style={{
                        color: "var(--text-muted)",
                      }}
                    />
                  </a>

                  {/* LINKEDIN */}

                  <a
                    href="https://www.linkedin.com/company/web-bocket/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="
                      group
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      hover:-translate-y-1
                    "
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      borderColor: "var(--border-primary)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--brand-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--brand-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--border-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--bg-tertiary)";
                    }}
                  >
                    <FaLinkedinIn
                      className="
                        text-base
                        transition-colors
                        duration-300
                        group-hover:text-black
                      "
                      style={{
                        color: "var(--text-muted)",
                      }}
                    />
                  </a>

                  {/* X */}

                  <a
                    href="https://x.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="X"
                    className="
                      group
                      flex
                      h-10
                      w-10
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      hover:-translate-y-1
                    "
                    style={{
                      backgroundColor: "var(--bg-tertiary)",
                      borderColor: "var(--border-primary)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--brand-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--brand-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--border-primary)";
                      e.currentTarget.style.backgroundColor =
                        "var(--bg-tertiary)";
                    }}
                  >
                    <FaXTwitter
                      className="
                        text-base
                        transition-colors
                        duration-300
                        group-hover:text-black
                      "
                      style={{
                        color: "var(--text-muted)",
                      }}
                    />
                  </a>
                </div>
              </div>

              {/* ========================================================
                  QUICK LINKS
              ======================================================== */}

              <div
                className="
                  min-w-0
                  flex
                  flex-col
                  items-center
                  text-center
                  sm:items-start
                  sm:text-left
                  lg:pl-10
                "
              >
                <h4
                  className="
                    mb-3
                    flex
                    items-center
                    justify-center
                    gap-2
                    text-sm
                    font-semibold
                    sm:mb-4
                    sm:justify-start
                    sm:text-base
                  "
                  style={{
                    color: "var(--text-primary)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--brand-primary)",
                    }}
                  >
                    •
                  </span>

                  <span>Quick Links</span>
                </h4>

                <ul
                  className="
                    flex
                    w-full
                    max-w-[190px]
                    flex-col
                    items-center
                    space-y-2.5
                    text-xs
                    sm:max-w-none
                    sm:items-start
                    sm:space-y-3
                    sm:text-sm
                    lg:ml-4
                  "
                  style={{
                    color: "var(--text-secondary)",
                  }}
                >
                  <HoverLi
                    onClick={() => handleNavigate("/about")}
                    className="hover:text-[var(--brand-primary)]"
                  >
                    About
                  </HoverLi>

                  <HoverLi
                    onClick={() => handleNavigate("/portfolio")}
                    className="hover:text-[var(--brand-primary)]"
                  >
                    Portfolio
                  </HoverLi>

                  <HoverLi
                    onClick={() => handleNavigate("/career")}
                    className="hover:text-[var(--brand-primary)]"
                  >
                    Career
                  </HoverLi>

                  <HoverLi
                    onClick={() => handleNavigate("/contact")}
                    className="hover:text-[var(--brand-primary)]"
                  >
                    Contact
                  </HoverLi>
                </ul>
              </div>

              {/* ========================================================
                  SERVICES
              ======================================================== */}

              <div
                className="
                  min-w-0
                  flex
                  flex-col
                  items-center
                  text-center
                  sm:items-start
                  sm:text-left
                  lg:pr-24
                "
              >
                <h4
                  className="
                    mb-3
                    flex
                    items-center
                    justify-center
                    gap-2
                    text-sm
                    font-semibold
                    sm:mb-4
                    sm:justify-start
                    sm:text-base
                  "
                  style={{
                    color: "var(--text-primary)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--brand-primary)",
                    }}
                  >
                    •
                  </span>

                  <span>Services</span>
                </h4>

                <div
                  className="
                    w-full
                    max-w-[235px]
                    sm:max-w-[245px]
                    lg:max-w-[250px]
                  "
                >
                  {serviceGroups.map((service) => {
                    const isOpen = openService === service.id;

                    return (
                      <div
                        key={service.id}
                        className="mb-1 w-full"
                      >
                        {/* MAIN SERVICE ROW */}

                        <div
                          className="
                            flex
                            min-h-[36px]
                            w-full
                            items-center
                            gap-2
                            rounded-lg
                            px-0
                            py-1.5
                            text-xs
                            transition-all
                            duration-300
                            sm:min-h-[38px]
                            sm:text-sm
                          "
                          style={{
                            color: "var(--text-secondary)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color =
                              "var(--brand-primary)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color =
                              "var(--text-secondary)";
                          }}
                        >
                          {/* SERVICE NAME */}

                          <button
                            type="button"
                            onClick={() =>
                              handleNavigate(service.path)
                            }
                            className="
                              min-w-0
                              flex-1
                              truncate
                              text-left
                              transition-colors
                              duration-300
                            "
                          >
                            {service.title}
                          </button>

                          {/* DROPDOWN */}

                          <button
                            type="button"
                            onClick={() =>
                              toggleService(service.id)
                            }
                            aria-label={`Toggle ${service.title} subservices`}
                            aria-expanded={isOpen}
                            className="
                              flex
                              h-7
                              w-7
                              shrink-0
                              items-center
                              justify-center
                              rounded-full
                              border
                              transition-all
                              duration-300
                              hover:text-black
                            "
                            style={{
                              borderColor:
                                "var(--border-primary)",
                              backgroundColor:
                                "var(--bg-tertiary)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor =
                                "var(--brand-primary)";
                              e.currentTarget.style.backgroundColor =
                                "var(--brand-primary)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor =
                                "var(--border-primary)";
                              e.currentTarget.style.backgroundColor =
                                "var(--bg-tertiary)";
                            }}
                          >
                            <motion.span
                              animate={{
                                rotate: isOpen ? 180 : 0,
                              }}
                              transition={{
                                duration: 0.25,
                              }}
                              className="flex"
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </motion.span>
                          </button>
                        </div>

                        {/* SUB SERVICES */}

                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{
                                height: 0,
                                opacity: 0,
                              }}
                              animate={{
                                height: "auto",
                                opacity: 1,
                              }}
                              exit={{
                                height: 0,
                                opacity: 0,
                              }}
                              transition={{
                                duration: 0.28,
                                ease: "easeOut",
                              }}
                              className="overflow-hidden"
                            >
                              <div
                                className="
                                  ml-1
                                  border-l
                                  py-1
                                  pl-3
                                  sm:ml-1.5
                                "
                                style={{
                                  borderColor:
                                    "color-mix(in srgb, var(--brand-primary) 20%, transparent)",
                                }}
                              >
                                {service.subServices.map(
                                  (subService) => (
                                    <motion.button
                                      key={subService.title}
                                      type="button"
                                      whileHover={{
                                        x: 4,
                                      }}
                                      transition={{
                                        duration: 0.2,
                                      }}
                                      onClick={() =>
                                        handleNavigate(
                                          subService.path
                                        )
                                      }
                                      className="
                                        block
                                        w-full
                                        py-1.5
                                        text-left
                                        text-[11px]
                                        leading-5
                                        transition-colors
                                        duration-300
                                        hover:text-[var(--brand-primary)]
                                        sm:text-xs
                                      "
                                      style={{
                                        color:
                                          "var(--text-muted)",
                                      }}
                                    >
                                      <span
                                        className="mr-1.5"
                                        style={{
                                          color:
                                            "var(--brand-primary)",
                                        }}
                                      >
                                        •
                                      </span>

                                      {subService.title}
                                    </motion.button>
                                  )
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}

                  {/* PRODUCT DEVELOPMENT */}

                  <div
                    className="
                      mt-1
                      flex
                      min-h-[36px]
                      w-full
                      items-center
                      gap-2
                      rounded-lg
                      py-1.5
                      text-xs
                      sm:min-h-[38px]
                      sm:text-sm
                    "
                    style={{
                      color: "var(--text-secondary)",
                    }}
                  >
                    <span className="min-w-0 flex-1 text-left">
                      Product Development
                    </span>

                    <span
                      className="
                        shrink-0
                        rounded-full
                        border
                        px-2
                        py-0.5
                        text-[8px]
                        font-semibold
                        uppercase
                        tracking-wider
                        sm:px-2.5
                        sm:py-1
                        sm:text-[9px]
                      "
                      style={{
                        color: "var(--brand-primary)",
                        borderColor:
                          "color-mix(in srgb, var(--brand-primary) 30%, transparent)",
                        backgroundColor:
                          "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                      }}
                    >
                      Soon
                    </span>
                  </div>
                </div>
              </div>

              {/* ========================================================
                  CONTACT INFO
              ======================================================== */}

              <div
                className="
                  min-w-0
                  flex
                  flex-col
                  items-center
                  text-center
                  sm:col-span-2
                  sm:items-center
                  sm:text-center
                  lg:col-span-1
                  lg:items-start
                  lg:text-left
                  lg:pl-4
                "
              >
                {/* CONTACT HEADING */}

                <h4
                  className="
                    mb-3
                    flex
                    items-center
                    justify-center
                    gap-2
                    text-sm
                    font-semibold
                    sm:mb-4
                    sm:text-base
                    lg:justify-start
                  "
                  style={{
                    color: "var(--text-primary)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--brand-primary)",
                    }}
                  >
                    •
                  </span>

                  <span>Contact Info</span>
                </h4>

                {/* CONTACT LIST */}

                <ul
                  className="
                    mx-auto
                    flex
                    w-full
                    max-w-full
                    flex-col
                    gap-3
                    ml-4
                    sm:text-sm
                    lg:mx-0
                    lg:ml-0
                  "
                  style={{
                    color: "var(--text-secondary)",
                  }}
                >
                  {/* ADDRESS */}

                  <HoverLi
                    className="
                      w-fit
                      max-w-full
                      hover:text-[var(--brand-primary)]
                    "
                  >
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Kolathia%20Square%2C%20near%20sri%20sai%20garden%2C%20uco%20bank%20top%20floor%2C%20khandagiri%2C%20Bhubaneswar%2C%20751030"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="
                        grid
                        w-fit
                        max-w-full
                        grid-cols-[20px_minmax(0,300px)]
                        items-start
                        gap-2
                        text-left
                      "
                    >
                      <span className="flex w-5 shrink-0 justify-center">
                        <MapPin
                          className="
                            mt-0.5
                            h-4
                            w-4
                            shrink-0
                          "
                          style={{
                            color: "var(--brand-primary)",
                          }}
                        />
                      </span>

                      <span
                        className="
                          min-w-0
                          leading-5
                        "
                      >
                        Kolathia Square, near Sri Sai
                        Garden, UCO Bank top floor,
                        Khandagiri, Bhubaneswar, 751030
                      </span>
                    </a>
                  </HoverLi>

                  {/* PHONE */}

                  <HoverLi
                    className="
                      w-fit
                      max-w-full
                      hover:text-[var(--brand-primary)]
                    "
                  >
                    <a
                      href="tel:+918328892097"
                      className="
                        grid
                        w-fit
                        max-w-full
                        grid-cols-[20px_minmax(0,1fr)]
                        items-center
                        gap-2
                        text-left
                      "
                    >
                      <span className="flex w-5 shrink-0 justify-center">
                        <Phone
                          className="
                            h-4
                            w-4
                            shrink-0
                          "
                          style={{
                            color: "var(--brand-primary)",
                          }}
                        />
                      </span>

                      <span className="min-w-0">
                        +91 83288 92097
                      </span>
                    </a>
                  </HoverLi>

                  {/* EMAIL */}

                  <HoverLi
                    className="
                      w-fit
                      max-w-full
                      hover:text-[var(--brand-primary)]
                    "
                  >
                    <a
                      href="mailto:web.bocket@gmail.com?subject=Website%20Inquiry&body=Hello%20Web%20Bocket%20Team,"
                      className="
                        grid
                        w-fit
                        max-w-full
                        grid-cols-[20px_minmax(0,1fr)]
                        items-center
                        gap-2
                        text-left
                      "
                    >
                      <span className="flex w-5 shrink-0 justify-center">
                        <Mail
                          className="
                            h-4
                            w-4
                            shrink-0
                          "
                          style={{
                            color: "var(--brand-primary)",
                          }}
                        />
                      </span>

                      <motion.span
                        whileHover={{ x: 5 }}
                        transition={{
                          duration: 0.28,
                          ease: "easeOut",
                        }}
                        className="
                          min-w-0
                          truncate
                        "
                      >
                        web.bocket@gmail.com
                      </motion.span>
                    </a>
                  </HoverLi>

                  {/* WORKING HOURS */}

                  <HoverLi
                    className="
                      w-fit
                      max-w-full
                      hover:text-[var(--brand-primary)]
                    "
                  >
                    <div
                      className="
                        grid
                        w-fit
                        max-w-full
                        grid-cols-[20px_minmax(0,1fr)]
                        items-center
                        gap-2
                        text-left
                      "
                    >
                      <span className="flex w-5 shrink-0 justify-center">
                        <Clock
                          className="
                            h-4
                            w-4
                            shrink-0
                          "
                          style={{
                            color: "var(--brand-primary)",
                          }}
                        />
                      </span>

                      <span className="min-w-0">
                        Mon-Sat 09:30am-06:30pm
                      </span>
                    </div>
                  </HoverLi>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            BOTTOM BAR
        ============================================================ */}

        <div
          className="
            relative
            z-10
            mx-auto
            flex
            w-full
            max-w-[1500px]
            flex-col
            items-center
            gap-3
            border-t
            pt-4
            text-center
            text-[10px]
            sm:flex-row
            sm:justify-between
            sm:gap-4
            sm:pt-5
            sm:text-left
            sm:text-xs
            md:text-sm
          "
          style={{
            borderColor: "var(--border-primary)",
            color: "var(--text-muted)",
          }}
        >
          {/* COPYRIGHT */}

          <div
            className="
              max-w-full
              leading-5
            "
          >
            Web_Bocket Pvt Ltd. © {currentYear}. All rights
            reserved.
          </div>

          {/* LEGAL LINKS */}

          <div
            className="
              flex
              flex-wrap
              items-center
              justify-center
              gap-x-2
              gap-y-1
              sm:justify-end
              sm:gap-x-3
            "
          >
            <button
              type="button"
              onClick={() =>
                handleNavigate("/privacy-policy")
              }
              className="
                whitespace-nowrap
                transition-colors
                duration-300
                hover:text-[var(--brand-primary)]
              "
            >
              Privacy &amp; Policy
            </button>

            <span
              style={{
                color: "var(--text-muted)",
              }}
            >
              •
            </span>

            <button
              type="button"
              onClick={() =>
                handleNavigate("/terms-conditions")
              }
              className="
                whitespace-nowrap
                transition-colors
                duration-300
                hover:text-[var(--brand-primary)]
              "
            >
              Terms &amp; Conditions
            </button>
          </div>
        </div>
      </footer>
    </>
  );
};

export default UserFooter;